import {
  isSuggestionPopupOpen,
  setSuggestionPopupOpen,
  setCloseHandler,
  clearCloseHandler
} from '../../suggestion-popup-state'
import type { Editor } from '@tiptap/core'
import { ReactRenderer } from '@tiptap/react'
import { SuggestionKeyDownProps } from '@tiptap/suggestion'
import tippy, { GetReferenceClientRect, Instance, Props } from 'tippy.js'
import type * as React from 'react'

type ListHandle = { onKeyDown: (args: SuggestionKeyDownProps) => boolean }

type RendererInstance = {
  element: HTMLElement
  ref: ListHandle | null
  destroy: () => void
  updateProps: (props: unknown) => void
}

type RenderProps = {
  editor: Editor
  range: { from: number; to: number }
  clientRect?: (() => DOMRect | null) | null
}

/**
 * The suggestion decoration doesn't render for an empty range (e.g. the args
 * suggestion right after a space), so fall back to the caret position.
 */
function getClientRect(props: RenderProps): GetReferenceClientRect {
  return () => {
    const rect = props.clientRect?.()
    if (rect) return rect
    const coords = props.editor.view.coordsAtPos(props.range.from)
    return new DOMRect(coords.left, coords.top, 0, coords.bottom - coords.top)
  }
}

const TippyRenderer = ReactRenderer as unknown as new (
  component: unknown,
  options: { props: unknown; editor: unknown }
) => RendererInstance

/**
 * Build the tippy-based render() object used by both slash suggestions,
 * mirroring src/components/PostEditor/PostTextarea/Mention/suggestion.ts.
 */
export function createSuggestionRender<P extends { items: unknown[] }>(
  List: React.ComponentType<any>
) {
  return () => {
    let component: RendererInstance | undefined
    let popup: Instance[] = []
    let touchListener: (e: TouchEvent) => void

    return {
      onBeforeStart: () => {
        touchListener = (e: TouchEvent) => {
          if (popup && popup[0] && isSuggestionPopupOpen()) {
            const popupElement = popup[0].popper
            if (popupElement && !popupElement.contains(e.target as Node)) {
              popup[0].hide()
            }
          }
        }
        document.addEventListener('touchstart', touchListener)

        setCloseHandler(() => {
          if (popup && popup[0]) popup[0].hide()
        })
      },
      onStart: (props: RenderProps) => {
        component = new TippyRenderer(List, {
          props,
          editor: props.editor
        })

        popup = tippy('body', {
          getReferenceClientRect: getClientRect(props),
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: 'manual',
          placement: 'bottom-start',
          hideOnClick: true,
          touch: true,
          onShow() {
            setSuggestionPopupOpen(true)
          },
          onHide() {
            setSuggestionPopupOpen(false)
          }
        })
      },

      onUpdate(props: RenderProps) {
        component?.updateProps(props as unknown as Partial<P>)

        popup[0]?.setProps({
          getReferenceClientRect: getClientRect(props)
        } as Partial<Props>)
      },

      onKeyDown(props: SuggestionKeyDownProps) {
        if (props.event.key === 'Escape') {
          popup[0]?.hide()
          return true
        }
        return component?.ref?.onKeyDown(props) ?? false
      },

      onExit() {
        setSuggestionPopupOpen(false)
        clearCloseHandler()
        popup[0]?.destroy()
        component?.destroy()

        document.removeEventListener('touchstart', touchListener)
      }
    }
  }
}
